import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

type UnsavedWorkContextValue = {
  hasUnsavedWork: boolean;
  setUnsavedWork: (id: string, isDirty: boolean) => void;
};

const UnsavedWorkContext = createContext<UnsavedWorkContextValue | null>(null);

export const UnsavedWorkProvider = ({ children }: { children: ReactNode }) => {
  const [dirtyIds, setDirtyIds] = useState<Set<string>>(new Set());
  const nextFormId = useRef(0);

  const setUnsavedWork = useCallback((id: string, isDirty: boolean) => {
    setDirtyIds((current) => {
      const next = new Set(current);
      if (isDirty) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ hasUnsavedWork: dirtyIds.size > 0, setUnsavedWork }),
    [dirtyIds, setUnsavedWork],
  );

  useEffect(() => {
    const forms = new Map<HTMLFormElement, string>();
    const getFormId = (form: HTMLFormElement) => {
      const existingId = forms.get(form);
      if (existingId) return existingId;

      const id = `form-${nextFormId.current}`;
      nextFormId.current += 1;
      forms.set(form, id);
      return id;
    };
    const isFormControl = (target: EventTarget | null) =>
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement;
    const handleInput = (event: Event) => {
      if (!isFormControl(event.target)) return;
      const form = (event.target as HTMLElement).closest("form");
      if (form) setUnsavedWork(getFormId(form), true);
    };
    const clearForm = (event: Event) => {
      const form = (event.target as HTMLElement).closest("form");
      if (!form) return;
      const id = forms.get(form);
      if (id) setUnsavedWork(id, false);
    };
    const pruneRemovedForms = () => {
      forms.forEach((id, form) => {
        if (!document.contains(form)) {
          forms.delete(form);
          setUnsavedWork(id, false);
        }
      });
    };
    const pruneInterval = window.setInterval(pruneRemovedForms, 10_000);

    document.addEventListener("input", handleInput, true);
    document.addEventListener("submit", clearForm, true);
    document.addEventListener("reset", clearForm, true);

    return () => {
      document.removeEventListener("input", handleInput, true);
      document.removeEventListener("submit", clearForm, true);
      document.removeEventListener("reset", clearForm, true);
      window.clearInterval(pruneInterval);
    };
  }, [setUnsavedWork]);

  return (
    <UnsavedWorkContext.Provider value={value}>
      {children}
    </UnsavedWorkContext.Provider>
  );
};

export const useUnsavedWork = () => {
  const context = useContext(UnsavedWorkContext);
  if (!context) {
    throw new Error("useUnsavedWork must be used inside UnsavedWorkProvider");
  }
  return context;
};
